import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { isEmpty, isPlainObject, isUndefined } from 'lodash'
import { buildMenu } from 'pages/Scenario/utils/table'
import {
  modelPermissionsSelector,
  updateStatusRow
} from 'pages/Scenario/utils/state'
import { runScenario, downloadError } from 'pages/Scenario/actions'

import Loading from '@ublique/components/Loading'
import { PlayFilled32 } from '@carbon/icons-react'

import ErrorLink from './progress/ErrorLink'
import OutputLink from './progress/OutputLink'
import RunningProgress from './progress/RunningProgress'
import ProgressWithLoading from './progress/ProgressWithLoading'
import useStoreSelector from 'utils/hooks/useStoreSelector'
import { ScenarioPermissions } from 'pages/Scenario/constants'
import { useOverflowMenuParent } from '../utils/table'

const { UPDATE } = ScenarioPermissions

const columnContent = {
  error: ErrorLink,
  loading: Loading.Inline,
  output: OutputLink,
  play: PlayFilled32,
  progress: ProgressWithLoading,
  timeProgress: RunningProgress
}

const ProgressColumnFramework = ({ api, data, value: { status } }) => {
  const history = useHistory()
  const { t } = useTranslation('Scenario')
  const { buffer, tableActions } = useStoreSelector('scenario')
  const modelPermissions = useStoreSelector(
    'appConfig',
    modelPermissionsSelector
  )

  const [progressData, setProgressData] = useState(null)
  const [scenarioStatus, setScenarioStatus] = useState(null)

  const { id, scenarioPermission: rowPermissions, outputPermission } = data

  const columnConfig = tableActions[scenarioStatus] || {}

  const showOutputPermission =
    columnConfig?.column === 'output'
      ? !isEmpty(outputPermission?.children)
      : true

  useEffect(() => {
    if (!isUndefined(buffer[id])) {
      const bufferData = buffer[id]
      setProgressData(bufferData)
      setScenarioStatus(bufferData.status)
    }
  }, [buffer[id]])

  useEffect(() => {
    if (
      !isUndefined(status) &&
      isUndefined(buffer[id]) &&
      status !== scenarioStatus
    ) {
      setScenarioStatus(status)
      updateStatusRow(id, status)
    }
  }, [status])

  /**
   * Use the 'getProps' function to get props from the outside (can be sync or async; here it's always converted into a Promise)
   * 'getProps' is remapped to 'getExternalPropsAsync', that is passed down to the component
   * Internally the component should always call 'getExternalPropsAsync' as a Promise, and wait for it
   */
  const contentProps = {
    progress: {
      max: progressData?.total,
      value: progressData?.progress,
      label: progressData?.step ?? null
    },
    play: {
      className:
        rowPermissions[UPDATE] && modelPermissions[UPDATE]
          ? 'play-btn'
          : 'play-btn-disabled',
      onClick: () => {
        if (rowPermissions[UPDATE] && modelPermissions[UPDATE]) {
          runScenario(data, t)
        }
      }
    },
    error: {
      labelText: t('Table.ProgressError'),
      downloadErrorFile: () => downloadError(data)
    },
    timeProgress: {
      getExternalPropsAsync: () =>
        Promise.resolve(columnConfig.column?.getProps?.(data))
    },
    output: {
      id,
      disabled: !showOutputPermission,
      labelText: t('Table.ProgressOutput')
    }
  }

  const renderContentByStatus = () => {
    const { column } = columnConfig
    const columnName = isPlainObject(column) ? column.name : column

    const Element = columnContent[columnName]
    const props = contentProps[columnName] || {}

    if (Element) {
      return <Element {...props} />
    }

    return null
  }

  const renderOverflowMenu = () => {
    if (scenarioStatus) {
      const config = columnConfig?.menuActions

      return buildMenu({
        data,
        gridApi: api,
        history,
        modelPermissions,
        config,
        showOutputPermission
      })
    }
    return null
  }

  useOverflowMenuParent()

  return (
    <div className='progress-column'>
      <div className='flex-wrap'>
        {scenarioStatus && !isEmpty(columnConfig) && renderContentByStatus()}
      </div>

      {!isEmpty(columnConfig) && renderOverflowMenu()}
    </div>
  )
}

export default ProgressColumnFramework
